import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import type {
  SupportCenterCancelReason,
  SupportCenterCancelReasonInput,
  SupportCenterCancelReasonKind,
} from '../../types/supportCenterCancelReasons'
import { SUPPORT_CENTER_CANCEL_REASON_KIND_LABELS } from '../../types/supportCenterCancelReasons'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { MODULE_NAME, getErrorMessage } from '../../components/centralAmparo/parametrosHelpers'
import ParametrosDeleteModal from './ParametrosDeleteModal'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

const ParametrosMotivosTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [kindTab, setKindTab] = React.useState<SupportCenterCancelReasonKind>('triagem')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<SupportCenterCancelReason[]>([])
  const [filter, setFilter] = React.useState('')

  const [formModal, setFormModal] = React.useState<SupportCenterCancelReasonInput | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await supportCenterCancelReasonsService.listByKind(kindTab)
      setRows(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [kindTab])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredRows = rows.filter((row) => {
    const term = filter.trim().toLocaleLowerCase('pt-BR')
    if (!term) return true
    return (
      row.name.toLocaleLowerCase('pt-BR').includes(term) ||
      row.description.toLocaleLowerCase('pt-BR').includes(term)
    )
  })

  const subTabClass = (tab: SupportCenterCancelReasonKind) =>
    `whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
      kindTab === tab
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const openNew = () => {
    setFormError(null)
    setFormModal({ name: '', description: '', kind: kindTab })
  }

  const openEdit = (row: SupportCenterCancelReason) => {
    setFormError(null)
    setFormModal({ id: row.id, name: row.name, description: row.description, kind: row.kind })
  }

  const closeForm = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleSave = async () => {
    if (!formModal || !user?.id) return
    if (!formModal.name.trim()) {
      setFormError('Informe o nome do motivo.')
      return
    }
    if (!formModal.description.trim()) {
      setFormError('Informe a descrição do motivo.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      const savedId = await supportCenterCancelReasonsService.upsert({
        ...formModal,
        kind: kindTab,
        name: formModal.name.trim(),
        description: formModal.description.trim(),
      })
      const kindLabel = SUPPORT_CENTER_CANCEL_REASON_KIND_LABELS[kindTab].toLowerCase()
      if (formModal.id) {
        void logUpdate(
          'support_center_cancel_reason',
          formModal.id,
          `Atualizou motivo de cancelamento (${kindLabel}): ${formModal.name.trim()}`
        )
      } else {
        void logCreate(
          'support_center_cancel_reason',
          savedId,
          `Criou motivo de cancelamento (${kindLabel}): ${formModal.name.trim()}`
        )
      }
      closeForm()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal || !user?.id) return
    setSubmittingDelete(true)
    try {
      await supportCenterCancelReasonsService.softDelete(
        deleteModal.id,
        deleteReason,
        user.id
      )
      const kindLabel = SUPPORT_CENTER_CANCEL_REASON_KIND_LABELS[kindTab].toLowerCase()
      void logDelete(
        'support_center_cancel_reason',
        deleteModal.id,
        `Excluiu motivo de cancelamento (${kindLabel}): ${deleteModal.label} — ${deleteReason.trim()}`
      )
      setDeleteModal(null)
      setDeleteReason('')
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      setDeleteModal(null)
    } finally {
      setSubmittingDelete(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <Text size="sm" color="muted">
          Cadastre os motivos usados na reprovação da triagem e no cancelamento do atendimento.
          As listas são independentes.
        </Text>

        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{pageError}</Text>
          </div>
        )}

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => {
                setKindTab('triagem')
                setFilter('')
              }}
              className={subTabClass('triagem')}
            >
              Triagem
            </button>
            <button
              type="button"
              onClick={() => {
                setKindTab('atendimento')
                setFilter('')
              }}
              className={subTabClass('atendimento')}
            >
              Atendimento
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
                  Motivos de cancelamento - {SUPPORT_CENTER_CANCEL_REASON_KIND_LABELS[kindTab]}
                </Heading>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredRows.length}
                  {filter.trim() && filteredRows.length !== rows.length
                    ? ` de ${rows.length}`
                    : ''}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filtrar por nome ou descrição..."
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                />
                {canInsert && (
                  <Button
                    type="button"
                    variant="solid"
                    onClick={openNew}
                    className="inline-flex items-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Novo motivo
                  </Button>
                )}
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <Text size="sm" color="muted">
                  {filter.trim()
                    ? 'Nenhum motivo encontrado com o filtro informado.'
                    : 'Nenhum motivo cadastrado.'}
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {row.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                          {row.description}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          <div className="inline-flex items-center gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="p-1.5 rounded text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                title="Editar"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => {
                                  setDeleteReason('')
                                  setDeleteModal({ id: row.id, label: row.name })
                                }}
                                className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                title="Excluir"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {formModal &&
        createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
              {formModal.id ? 'Editar motivo' : 'Novo motivo'}
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Lista: {SUPPORT_CENTER_CANCEL_REASON_KIND_LABELS[kindTab]}
            </Text>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={inputClass}
              value={formModal.name}
              onChange={(e) => setFormModal({ ...formModal, name: e.target.value })}
              placeholder="Ex.: Paciente desistiu"
              autoFocus
            />
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-1">
              Descrição <span className="text-red-500">*</span>
            </label>
            <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-1">
              Descrição do motivo do cancelamento que o segurado/paciente irá receber
            </p>
            <textarea
              rows={3}
              className={`${inputClass} resize-none`}
              value={formModal.description}
              onChange={(e) => setFormModal({ ...formModal, description: e.target.value })}
              placeholder="Texto que o segurado/paciente irá receber"
            />
            {formError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {formError}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeForm} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={
                  submittingForm || !formModal.name.trim() || !formModal.description.trim()
                }
              >
                {submittingForm ? 'Salvando…' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={() => {
            if (submittingDelete) return
            setDeleteModal(null)
            setDeleteReason('')
          }}
          onConfirm={() => void handleDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosMotivosTab
